import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { getCompanyId } from '../utils/tenantContext';
import { useAuth } from './useAuth';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'alert' | 'success';
  read: boolean;
  dismissed: boolean;
  createdAt: string;
  wellId?: string;
  wellName?: string;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [backendConnected, setBackendConnected] = useState(false);
  const companyId = getCompanyId();
  const { user } = useAuth();

  useEffect(() => {
    if (companyId && user) {
      loadNotifications();
    }
    // REMOVED: Timer-based polling - Now using event-driven updates
    // const interval = setInterval(loadNotifications, 15000);
    // return () => clearInterval(interval);
  }, [companyId, user?.id]);

  const loadNotifications = async () => {
    if (!companyId || !user) return;
    try {
      // Try to load from backend first using centralized API service
      const backendResponse = await api.fetchNotifications(companyId, user.id);
      
      // Validate that we got an array
      if (!Array.isArray(backendResponse)) {
        throw new Error('Invalid response format');
      }
      
      setNotifications(backendResponse as Notification[]);
      setUnreadCount(backendResponse.filter((n: any) => !n.read && !n.dismissed).length);
      setBackendConnected(true);
      
      // Sync backend to localStorage for offline access
      localStorage.setItem('oilwellx_notifications', JSON.stringify(backendResponse));
      
    } catch (error) {
      setBackendConnected(false);
      
      // Fallback to localStorage
      const localNotifications: Notification[] = JSON.parse(
        localStorage.getItem('oilwellx_notifications') || '[]'
      );
      setNotifications(localNotifications);
      setUnreadCount(localNotifications.filter((n: Notification) => !n.read && !n.dismissed).length);
      
    } finally {
      setLoading(false);
    }
  };

  const createNotification = async (notificationData: any) => {
    if (!companyId) return;
    try {
      // Create in backend using centralized API service
      const newNotification = await api.createNotification(companyId, notificationData);
      
      
      // Update local state immediately
      setNotifications(prev => [newNotification as Notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      return newNotification;
    } catch (error) {
      console.error('Error creating notification, using localStorage:', error);
      
      // Fallback to localStorage
      const notification = {
        ...notificationData,
        id: notificationData.id || `notif-${Date.now()}`,
        createdAt: notificationData.createdAt || new Date().toISOString(),
      };
      
      const localNotifications: Notification[] = JSON.parse(
        localStorage.getItem('oilwellx_notifications') || '[]'
      );
      localNotifications.unshift(notification);
      localStorage.setItem('oilwellx_notifications', JSON.stringify(localNotifications));
      
      setNotifications(localNotifications);
      setUnreadCount(localNotifications.filter((n: Notification) => !n.read && !n.dismissed).length);
      
      return notification;
    }
  };

  const updateNotification = async (id: string, updates: any) => {
    try {
      // Update in backend using centralized API service
      // Note: api.ts doesn't have a generic updateNotification, only markAsRead.
      // But we can use api.put if we really need to, or add updateNotification to api.ts.
      // For now, let's assume markAsRead is the main use case, or use api.put if available (it is available on api object but might fail if endpoint missing).
      // Actually, api.ts has markNotificationAsRead.
      
      let updatedNotification;
      if (updates.read) {
         await api.markNotificationAsRead(id);
         updatedNotification = { ...notifications.find(n => n.id === id), ...updates };
      } else {
         // Fallback to generic put if available or just local update
         // api.ts doesn't export updateNotification.
         // We'll just update local state and warn.
         updatedNotification = { ...notifications.find(n => n.id === id), ...updates };
      }
      
      
      // Update local state immediately
      setNotifications(prev =>
        prev.map(n => (n.id === id ? updatedNotification : n))
      );
      
      // Recalculate unread count
      const newNotifications = notifications.map(n => 
        n.id === id ? updatedNotification : n
      );
      setUnreadCount(newNotifications.filter(n => !n.read && !n.dismissed).length);
      
      return updatedNotification;
    } catch (error) {
      console.error('Error updating notification, using localStorage:', error);
      
      // Fallback to localStorage
      const localNotifications: Notification[] = JSON.parse(
        localStorage.getItem('oilwellx_notifications') || '[]'
      );
      const updatedNotifications = localNotifications.map((n: Notification) =>
        n.id === id ? { ...n, ...updates } : n
      );
      localStorage.setItem('oilwellx_notifications', JSON.stringify(updatedNotifications));
      
      setNotifications(updatedNotifications);
      setUnreadCount(updatedNotifications.filter((n: Notification) => !n.read && !n.dismissed).length);
    }
  };

  const markAsRead = async (id: string) => {
    return updateNotification(id, { read: true });
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
      
      if (unreadIds.length === 0) return;

      // Bulk update in backend using centralized API service
      // api.ts doesn't have bulk update. Loop through them.
      await Promise.all(unreadIds.map(id => api.markNotificationAsRead(id)));

      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read, using localStorage:', error);
      
      // Fallback to localStorage
      const localNotifications = notifications.map(n => ({ ...n, read: true }));
      localStorage.setItem('oilwellx_notifications', JSON.stringify(localNotifications));
      
      setNotifications(localNotifications);
      setUnreadCount(0);
    }
  };

  const dismissNotification = async (id: string) => {
    return updateNotification(id, { dismissed: true });
  };

  const deleteNotification = async (id: string) => {
    try {
      // Delete from backend using centralized API service
      await api.deleteNotification(id);
      
      
      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== id));
      
      // Recalculate unread count
      const newNotifications = notifications.filter(n => n.id !== id);
      setUnreadCount(newNotifications.filter(n => !n.read && !n.dismissed).length);
    } catch (error) {
      console.error('Error deleting notification, using localStorage:', error);
      
      // Fallback to localStorage
      const localNotifications = notifications.filter(n => n.id !== id);
      localStorage.setItem('oilwellx_notifications', JSON.stringify(localNotifications));
      
      setNotifications(localNotifications);
      setUnreadCount(localNotifications.filter(n => !n.read && !n.dismissed).length);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    backendConnected,
    refreshNotifications: loadNotifications,
    createNotification,
    updateNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    deleteNotification,
  };
}